import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	DestroyRef, ElementRef,
	EventEmitter,
	inject, Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NavigationMenuItem } from '@itc-core/components/navigation-panels/navigation-menu/common/navigation-menu.interfaces';
import { PopoverComponent } from '@mos-core/components/popover/popover.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { S3Service } from '../../services/s3.service';
import { AuthUserModel } from '../../models';
import { ProjectModel, UserModel } from '../../models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectService } from '../../services/project.service';
import { Project, ProjectsFullListSearchResponse } from '../../interfaces';
import { debounceTime, filter, Subject } from 'rxjs';
import { NavigationService } from '../../services/navigation.service';
import { AuthService } from '../../services/auth.service';
import { AlertService } from '../../pages/alert/common/alert.service';
import { environment } from '../../../environments/environment';
import { SelectedProjectService } from '../../services/select-project.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { Feature } from '../../helpers/feature';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { getSteps as welcomeSteps } from './welcome.tour.config';
import { UserService } from '../../services/user.service';
import { ProductTourService } from '../../services/product-tour.service';
import { ShepherdService } from 'angular-shepherd';
import { getSteps as defaultUserSteps } from '../sidebar/user-menu.tour.config';
import { getSteps as defaultSideBarSteps } from '../sidebar/sidebar.tour.config';
import { MeasureSystemService } from '../../services/measure-system.service';
import { ProjectRequestService } from '../../services/project-request.service';
import {
	BadgeDollarSign,
	BellIcon,
	CalendarIcon,
	ChartColumn,
	FileIcon,
	FolderClosed,
	HomeIcon, LifeBuoyIcon, MagnetIcon,
	PaintbrushVertical, SettingsIcon, TableIcon, UserIcon, Users,
} from 'lucide-angular';
import { NavRoute } from '../../interfaces/nav-route';
import { TEXT } from '../../helpers';
import { StringHelper } from '../../helpers/string-helper';

@Component({
	selector: 'paint-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, AfterViewInit {
	@Output() public navigationItemClicked: EventEmitter<NavigationMenuItem> = new EventEmitter();
	@Output() public commandPaletteOpened: EventEmitter<void> = new EventEmitter();
	@ViewChild('userPopover') public userPopover!: PopoverComponent;
	@ViewChild('projectPopover') public projectPopover!: PopoverComponent;
	@ViewChild('mobileProjectPopover') public mobileProjectPopover!: PopoverComponent;
	@ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

	@Input() public canCreateNewProject: any;

	public isProjectExpanded: boolean = true;
	public navRoutes: NavRoute[] = [];
	public projectRoutes: NavRoute[] = [];
	public userRoutes: NavRoute[] = [];
	public currentUser: UserModel;
	public userFullName: string;
	public initials: string;
	public avatarUrl: string | null = null;
	public projects: (ProjectModel | Project)[] = [];
	public loadingProjects: boolean = false;
	public destroyRef = inject(DestroyRef);
	public selectedProject: Project;
	private searchSubject = new Subject<string>();
	public fuzzySearchString: string = '';
	public emptyState: boolean = false;
	public isCollapsed: boolean = false;
	public isHovered: boolean = false;
	public isEntityOwner: boolean = false;
	public totalAlerts: number = 0;
	public userRole: string;
	public isEntityActive: boolean = false;
	public isMobileMenuOpen: boolean = false;
	public isContractsAndAbove: boolean = false;
	public isNewProjectButtonDisabled: boolean = false;
	public selectedEnvironment = environment.env;
	public searchHotKey = 'Ctrl + K';
	public projectRequestCount = 0;
	public textConstants: typeof TEXT = TEXT;

	// Feature or Route Permissions
	public canUploadCustomDocuments: boolean = false;
	public hasMeasuringToolAccess: boolean = false;

	constructor(private router: Router,
				private localStorageService: LocalStorageService,
				private s3Service: S3Service,
				private cdr: ChangeDetectorRef,
				private projectService: ProjectService,
				private navigationService: NavigationService,
				private authService: AuthService,
				private alertService: AlertService,
				private selectedProjectService: SelectedProjectService,
				private featureFlagService: FeatureFlagService,
				public rolePermissions: RolePermissionsService,
				private userService: UserService,
				private productTourService: ProductTourService,
				private shepherdService: ShepherdService,
				protected measureSystemService: MeasureSystemService,
				private projectRequestService: ProjectRequestService,
	) {
		this.searchSubject.pipe(
			debounceTime(200),
			takeUntilDestroyed(this.destroyRef),
		).subscribe(searchTerm => {
			this.fuzzySearchString = searchTerm;
			this.fuzzySearch();
		});
	}

	public ngOnInit(): void {
		// Initialise user, projects and routes
		this.initialiseUser();
		this.initialiseStandardRoutes();
		this.initialiseUserRoutes();
		this.getLocalProject();

		// Detect logout to clear selectedProject
		this.authService.userDetailsObservable.pipe(
			takeUntilDestroyed(this.destroyRef),
		).subscribe((userDetails: AuthUserModel) => {
			if (!userDetails) {
				this.selectedProjectService.clearSelectedProject();
				this.initialiseProjectRoutes();
			}
		});

		// Collapse expanded routes on navigation
		this.router.events.pipe(
			filter(event => event instanceof NavigationEnd),
			takeUntilDestroyed(this.destroyRef),
		).subscribe((event: NavigationEnd) => {
			// Check if the URL contains '/project/:id'
			const projectIdMatch = event.urlAfterRedirects.match(/\/project\/([^/]+)/);
			if (projectIdMatch && projectIdMatch[1]) {
				const projectId = projectIdMatch[1];
				if (!this.selectedProject || this.selectedProject && this.selectedProject?.id !== projectId) {
					this.projectService.getProject(projectId)
						.pipe(takeUntilDestroyed(this.destroyRef))
						.subscribe(selectProject => {
								this.setSelectedProject(selectProject);
								this.initialiseProjectRoutes();
							},
						);
				}
			}
			this.collapseExpandedRoutes();
		});

		// Get total alerts
		this.alertService.alerts.subscribe(total => {
			this.totalAlerts = total;
		});

		if (navigator.appVersion.indexOf('Mac') !== -1) {
			this.searchHotKey = '⌘K';
		} else {
			this.searchHotKey = 'Ctrl + K';
		}

		this.projectRequestService.requests.pipe(
			takeUntilDestroyed(this.destroyRef))
			.subscribe(count => {
				this.projectRequestCount = count;
			});
	}

	public ngAfterViewInit() {
		if (!this.currentUser || this.currentUser.entity.subscriptionLevel === 'INVALID') {
			return;
		}

		this.userService
			.getUserTours(this.currentUser.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: any) => {
				if (!response.tours.Welcome && !this.shepherdService.isActive && this.userRole === 'superAdmin') {
					this.isCollapsed = false;
					this.productTourService.setTour('Welcome');
					this.productTourService.showSteps(welcomeSteps(this.shepherdService, this.productTourService), true);
				}
			});

		this.productTourService.activeTourSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(tourId => {
			if (tourId) {
				this.isCollapsed = false;
				this.shepherdService.defaultStepOptions = {
					scrollTo: true,
					cancelIcon: {
						enabled: true,
					},
				};
				this.shepherdService.modal = true;
				const relatedTours = ['Account', 'Companies', 'Users', 'Divisions', 'Help'];
				const scheduleToolTours: string[] = ['My Shifts', 'Project/Location', 'Resources', 'Schedule', 'Timesheets'];
				if (!this.shepherdService.isActive) {
					if (relatedTours.includes(tourId)) {
						this.productTourService.showSteps(defaultUserSteps(this.router, this.shepherdService, this.productTourService), false);
					} else if (scheduleToolTours.includes(tourId) || tourId === 'Clients') {
						this.productTourService.showSteps(defaultSideBarSteps(this.productTourService, this.shepherdService), false);
					}
				}
			} else if (!this.shepherdService.isActive) {
				// Persist sidebar state using local storage when tour is not active
				const sidebarState = localStorage.getItem('sidebarCollapsed');
				this.isCollapsed = sidebarState === 'true';
			}
		});

		this.router.events
			.pipe(
				filter(event => event instanceof NavigationStart),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((event: NavigationStart) => {
				if (event.navigationTrigger === 'popstate' && this.shepherdService.isActive) {
					this.shepherdService.cancel();
				}
			});

		this.initialiseStandardRoutes();
	}

	private getLocalProject() {
		this.selectedProjectService.getSelectedProject()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(project => {
				this.selectedProject = project;
				this.initialiseProjectRoutes();
			});
	}

	private initialiseUser() {
		this.currentUser = this.localStorageService.getUserDetails();
		this.userRole = this.localStorageService.getUserDetails().role;
		this.isEntityOwner = this.authService.isEntityOwner();
		this.isEntityActive = this.authService.isEntityActive();
		this.userFullName = this.currentUser.fullName;
		this.isContractsAndAbove = this.rolePermissions.isContractsAndAbove();
		this.isNewProjectButtonDisabled = !(this.rolePermissions.isTrainee() || this.rolePermissions.isUser() || this.rolePermissions.isTeamLeaderAndAbove()) || !this.canCreateNewProject;
		this.getPictureUrl();
	}

	private initialiseProjectRoutes() {
		this.hasMeasuringToolAccess = this.currentUser.entity?.hasMeasuringToolAccess;

		this.projectRoutes = [
			{ name: 'Overview', route: `project/${this.selectedProject?.id}/overview` },
			{ name: 'Charges', route: `project/${this.selectedProject?.id}/charges` },
			{ name: 'Variations', route: `project/${this.selectedProject?.id}/variations` },
			{
				name: 'Measuring Tool',
				route: `measuring-tool/${this.selectedProject?.id}`,
				disabled: !this.hasMeasuringToolAccess,
				hidden: false,
			},
			{ name: 'Applications Rates', route: `project/${this.selectedProject?.id}/application-rates` },
			{ name: 'Scope', route: `project/${this.selectedProject?.id}/scope` },
			{ name: 'Labour Costs', route: `project/${this.selectedProject?.id}/labour-costs` },
			{ name: 'Discounts', route: `project/${this.selectedProject?.id}/discounts` },
			{
				name: 'Export Documents',
				route: `project/${this.selectedProject?.id}/export`,
				subRoutes: [
					{ name: 'Tender Document', route: `project/${this.selectedProject?.id}/export/tender-document` },
					{ name: 'Quote Proposal', route: `project/${this.selectedProject?.id}/export/quote-proposal` },
					{ name: 'Office Folder', route: `project/${this.selectedProject?.id}/export/office-folder` },
					{ name: 'Site Folder', route: `project/${this.selectedProject?.id}/export/site-folder` },
					{ name: 'Safety Folder', route: `project/${this.selectedProject?.id}/export/safety-folder` },
					{ name: 'Products Folder', route: `project/${this.selectedProject?.id}/export/product-folder` },
					{ name: 'QA Folder', route: `project/${this.selectedProject?.id}/export/qa-folder` },
					{ name: 'Policies', route: `project/${this.selectedProject?.id}/export/policies` },
					{ name: 'Procedures', route: `project/${this.selectedProject?.id}/export/procedures` },
					{ name: 'Variation Quote', route: `project/${this.selectedProject?.id}/export/variation-quote` },
					{ name: 'Quote', route: `project/${this.selectedProject?.id}/export/quote` },
					{ name: 'Bill of Quantity', route: `project/${this.selectedProject?.id}/export/boq-export` },
				],
			},
			{ name: 'Comments', route: `project/${this.selectedProject?.id}/comment` },
			{ name: 'Attachments', route: `project/${this.selectedProject?.id}/attachments` },
			{ name: 'Emails/Approvals', route: `project/${this.selectedProject?.id}/emails` },
			{ name: 'Tasks', route: `project/${this.selectedProject?.id}/tasks` },
			{ name: 'Time', route: `project/${this.selectedProject?.id}/time` },
			{ name: 'Project Invoicing', route: `project/${this.selectedProject?.id}/invoice` },
			{ name: 'Project Costs', route: `project/${this.selectedProject?.id}/costs` },
			{ name: 'Versions', route: `project/${this.selectedProject?.id}/versions` },
		];
		this.cdr.detectChanges();
	}

	private initialiseStandardRoutes() {
		this.canUploadCustomDocuments = this.currentUser.entity?.canUploadCustomDocuments;

		this.navRoutes = [
			{ name: 'Dashboard', route: '/', icon: HomeIcon, disabled: false, hidden: false },
			{ name: 'Projects', route: '/projects', icon: FolderClosed, disabled: false, hidden: false },
			{
				name: 'Live',
				route: '/live',
				icon: PaintbrushVertical,
				hidden: !this.isContractsAndAbove,
				subRoutes: [
					{ name: 'Live Projects', route: '/live/projects' },
					{ name: 'Project Forecasting', route: '/live/forecasting' },
				],
			},
			{
				name: 'Leads',
				route: '/leads',
				subRoutes: [
					{ name: 'Project Requests', route: '/leads/project-requests' },
					{ name: 'Targeting', route: '/leads/targeting' },
				],
				icon: MagnetIcon,
				disabled: false,
				hidden: false,
			},
			{ name: 'Clients', route: '/clients', icon: Users, disabled: false, hidden: false },
			{
				name: 'Sales',
				route: '/sales',
				subRoutes: [
					{ name: 'Reports', route: '/sales/reports' },
					{ name: 'Budgets', route: '/sales/budgets' },
					{ name: StringHelper.capitalize(this.textConstants.allocations), route: '/sales/allocations' },
				],
				icon: ChartColumn,
				disabled: !this.featureFlagService.has(Feature.Reporting),
				hidden: false,
			},
			{
				name: 'Invoices',
				route: '/invoices',
				icon: FileIcon,
				disabled: false,
				hidden: !this.isContractsAndAbove,
			},
			{
				name: 'Scheduler',
				route: '/schedule',
				subRoutes: [
					{ name: 'Project Location', route: '/schedule/projects' },
					{ name: 'Schedule', route: '/schedule/schedule-view' },
					{ name: 'Resources', route: '/schedule/resources' },
					{ name: 'My Shifts', route: '/shifts' },
				],
				isExpanded: false,
				icon: CalendarIcon,
				disabled: !this.featureFlagService.has(Feature.ScheduleTool),
				hidden: false,
			},
			{
				name: 'Help Center',
				route: '/help-centre',
				icon: LifeBuoyIcon,
				disabled: false,
			},
		];
	}

	private initialiseUserRoutes() {
		this.userRoutes = [
			{ name: 'Alerts', route: '/alerts', icon: BellIcon },
			{ name: 'Profile', route: '/user', icon: UserIcon },
			{ name: 'Settings', route: '/admin/account-details', icon: SettingsIcon },
			{
				name: 'Libraries',
				route: '/libraries',
				icon: TableIcon,
				hidden: !this.isContractsAndAbove,
			},
		];

		if (this.isEntityOwner) {
			this.userRoutes.push({ name: 'Subscription', route: '/subscription', icon: BadgeDollarSign });
		}
	}

	public userMenuSelection(route: NavRoute, event: MouseEvent) {
		event.stopPropagation();
		if (route.route.startsWith('https')) {
			window.open(route.route, '_blank');
		} else {
			this.navigationService.setRoute([route.route]);
		}
		this.userPopover.close();
		this.toggleMobileMenu();
	}

	public onSearch(event: any) {
		this.fuzzySearchString = event.target.value.trim().toLowerCase();
		this.emptyState = false;
		this.projects = [];

		if (!this.fuzzySearchString || this.fuzzySearchString.length === 0) {
			this.projects = [];
			this.emptyState = false;
			this.loadingProjects = false;
			return;
		}

		if (this.fuzzySearchString === '?') {
			this.emptyState = false;
			return;
		}

		this.searchSubject.next(this.fuzzySearchString);
	}

	protected visitHelpCenter() {
		this.router.navigate(['/help-centre']);
	}

	public fuzzySearch(): void {
		this.loadingProjects = true;
		this.projectService.getFullProjectList(this.fuzzySearchString)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: ProjectsFullListSearchResponse) => {
				if (result.projects && result.projects.length > 0) {
					this.projects = result.projects;
					this.emptyState = false;
					this.loadingProjects = false;
				} else {
					this.projects = [];
					this.emptyState = true;
					this.loadingProjects = false;
				}
			});
	}

	public setSelectedProject(project: Project): void {
		this.selectedProject = project;
		this.selectedProjectService.setSelectedProject(project);
		this.mobileProjectPopover.close();

		const currentUrl = this.router.url;

		if (currentUrl.includes('project')) {
			const path = currentUrl.split('/').pop();

			this.router.navigate([`/project/${project.id}/${path}`]);
		} else {
			this.router.navigate([`/project/${project.id}/overview`]);
		}
	}

	public expandProjectRoutes(): void {
		this.isProjectExpanded = !this.isProjectExpanded;
	}

	public handleNavigation(route: NavRoute): void {
		if (route.disabled) {
			this.router.navigate(['/subscription']);
			this.toggleMobileMenu();
			return;
		}

		if (route.subRoutes && !route.disabled) {
			route.isExpanded = !route.isExpanded;
		} else {
			this.router.navigate([route.route]);
			this.toggleMobileMenu();
		}
	}

	public handleProjectNavigation(route: NavRoute): void {
		if (route.disabled) {
			this.router.navigate(['/subscription']);
			this.toggleMobileMenu();
			return;
		}

		if (route.subRoutes) {
			this.toggleSubRoutes(route, new Event('click'));
		} else {
			this.router.navigate([route.route]);
			this.toggleMobileMenu();
		}
	}

	public toggleSubRoutes(route: NavRoute, event: Event): void {
		event.stopPropagation();
		if (!route.disabled) {
			route.isExpanded = !route.isExpanded;
		}
		this.cdr.detectChanges();
	}

	private collapseExpandedRoutes() {
		const currentUrl = this.router.url;
		this.navRoutes.forEach(route => {
			if (route.subRoutes) {
				if (!currentUrl.startsWith(route.route)) {
					route.isExpanded = false;
				}
			}
		});
		this.cdr.detectChanges();
	}

	public openUserPopover(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();
		this.userPopover.open(event);
	}

	public openMobileProjectPopover(event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();
		this.mobileProjectPopover.open(event);
	}

	public getPictureUrl() {
		if (this.currentUser && this.currentUser.displayPictureKey) {
			this.s3Service.getSignedUrl(this.currentUser.displayPictureKey).subscribe(url => {
				this.avatarUrl = url;
				this.cdr.detectChanges();
			});
		} else {
			this.getInitials();
		}
	}

	public getInitials(): string {
		if (!this.currentUser || !this.userFullName) {
			return '';
		}
		const [firstName, lastName] = this.currentUser.fullName.split(' ');
		this.initials = `${firstName[0]} ${lastName[0]}`;
	}

	public toggleSidebar(event: MouseEvent): void {
		event.stopPropagation();
		this.isCollapsed = !this.isCollapsed;
		localStorage.setItem('sidebarCollapsed', this.isCollapsed.toString());
		if (this.isCollapsed) {
			this.isHovered = false;
		}
		this.cdr.detectChanges();
	}

	public onMouseEnter(): void {
		if (this.isCollapsed) {
			this.isHovered = true;
			this.cdr.detectChanges();
		}
	}

	public onMouseLeave(): void {
		if (this.isCollapsed) {
			this.isHovered = false;
			this.cdr.detectChanges();
		}
	}

	public toggleMobileMenu() {
		this.isMobileMenuOpen = !this.isMobileMenuOpen;
	}

	public closeMobileMenu() {
		this.isMobileMenuOpen = false;
	}

	public openCommandPalette() {
		this.commandPaletteOpened.emit();
	}

	protected readonly environment = environment;
}
